import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '../utils/constants';
import IntakePhotoThumbnails from '../components/shared/IntakePhotoThumbnails';

const SalesLandingPage = ({ onNavigate, currentUser, onBack }) => {
  const [stats, setStats] = useState({
    totalEstimates: 0,
    pendingEstimates: 0,
    approvedEstimates: 0,
    totalValue: 0,
    totalInvoices: 0,
    outstandingBalance: 0
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [estimatesRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/sales/estimates`),
        axios.get(`${API}/sales/invoices`)
      ]);
      const estimates = estimatesRes.data;
      const invoices = invoicesRes.data;
      const outstandingBalance = invoices.reduce((sum, inv) => sum + (inv.balance_due || 0), 0);

      setStats({
        totalEstimates: estimates.length,
        pendingEstimates: estimates.filter(e => e.status === 'ISSUED' || e.status === 'DRAFT').length,
        approvedEstimates: estimates.filter(e => e.status === 'APPROVED').length,
        totalValue: estimates.reduce((sum, e) => sum + e.total, 0),
        totalInvoices: invoices.length,
        outstandingBalance
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={onBack}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors"
                data-testid="sales-landing-back"
              >
                ← Back to Gears
              </button>
              <h1 className="text-4xl font-bold text-gray-800">💼 Sales</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="container mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="sales-stats-total-estimates">
            <div className="text-sm text-gray-600 mb-1">Total Estimates</div>
            <div className="text-3xl font-bold text-gray-800" data-testid="sales-stats-total-estimates-value">{stats.totalEstimates}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="sales-stats-pending-estimates">
            <div className="text-sm text-gray-600 mb-1">Pending</div>
            <div className="text-3xl font-bold text-orange-600" data-testid="sales-stats-pending-estimates-value">{stats.pendingEstimates}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="sales-stats-approved-estimates">
            <div className="text-sm text-gray-600 mb-1">Approved</div>
            <div className="text-3xl font-bold text-green-600" data-testid="sales-stats-approved-estimates-value">{stats.approvedEstimates}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="sales-stats-estimate-value">
            <div className="text-sm text-gray-600 mb-1">Total Estimate Value</div>
            <div className="text-3xl font-bold text-gray-800" data-testid="sales-stats-estimate-value-amount">${formatCurrency(stats.totalValue)}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="sales-stats-total-invoices">
            <div className="text-sm text-gray-600 mb-1">Total Invoices</div>
            <div className="text-3xl font-bold text-gray-800" data-testid="sales-stats-total-invoices-value">{stats.totalInvoices}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="sales-stats-outstanding-balance">
            <div className="text-sm text-gray-600 mb-1">Outstanding Balance</div>
            <div className="text-3xl font-bold text-red-600" data-testid="sales-stats-outstanding-balance-value">${formatCurrency(stats.outstandingBalance)}</div>
          </div>
        </div>

        {/* Navigation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <button
            onClick={() => onNavigate('estimates')}
            className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-8 text-white text-left hover:shadow-xl transition-all transform hover:scale-105"
            data-testid="sales-nav-estimates"
          >
            <div className="text-5xl mb-4">📋</div>
            <h2 className="text-2xl font-bold mb-2">Estimates</h2>
            <p className="text-blue-100">Create, send, and track estimates</p>
          </button>

          <button
            onClick={() => onNavigate('invoices')}
            className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-8 text-white text-left hover:shadow-xl transition-all transform hover:scale-105"
            data-testid="sales-nav-invoices"
          >
            <div className="text-5xl mb-4">🧾</div>
            <h2 className="text-2xl font-bold mb-2">Invoices</h2>
            <p className="text-emerald-100">Send invoices & record payments</p>
          </button>

          <button
            onClick={() => onNavigate('clients')}
            className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-8 text-white text-left hover:shadow-xl transition-all transform hover:scale-105"
            data-testid="sales-nav-clients"
          >
            <div className="text-5xl mb-4">👤</div>
            <h2 className="text-2xl font-bold mb-2">Customers</h2>
            <p className="text-indigo-100">Save and edit client records</p>
          </button>

          <button
            onClick={() => onNavigate('reports')}
            className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-8 text-white text-left hover:shadow-xl transition-all transform hover:scale-105"
            data-testid="sales-nav-reports"
          >
            <div className="text-5xl mb-4">📈</div>
            <h2 className="text-2xl font-bold mb-2">Reports</h2>
            <p className="text-amber-100">Estimate and invoice analytics</p>
          </button>

          <button
            onClick={() => onNavigate('crm')}
            className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-8 text-white text-left hover:shadow-xl transition-all transform hover:scale-105"
            data-testid="sales-nav-crm"
          >
            <div className="text-5xl mb-4">👥</div>
            <h2 className="text-2xl font-bold mb-2">CRM</h2>
            <p className="text-green-100">Manage leads & follow-ups</p>
          </button>

          <button
            onClick={() => onNavigate('settings')}
            className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-8 text-white text-left hover:shadow-xl transition-all transform hover:scale-105"
            data-testid="sales-nav-settings"
          >
            <div className="text-5xl mb-4">⚙️</div>
            <h2 className="text-2xl font-bold mb-2">Settings</h2>
            <p className="text-purple-100">Company, items, contracts</p>
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== CRM / Leads Module (under Sales gear) =====

const DEFAULT_TAG_OPTIONS = [
  'New',
  'Hot',
  'Cold',
  'Tire Kicker',
  'Hired',
  'Declined',
  'Maybe',
];

const DIVISION_OPTIONS = [
  { value: '', label: 'All divisions' },
  { value: 'foundation', label: 'Foundation' },
  { value: 'waterproofing', label: 'Waterproofing' },
  { value: 'septic', label: 'Septic' },
];

const CRMModule = ({ onBack, currentUser }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [filterDivision, setFilterDivision] = useState('');
  const [leadsPage, setLeadsPage] = useState(1);
  const LEADS_PER_PAGE = 50;
  const [tagOptions, setTagOptions] = useState([]);
  const [showTagManager, setShowTagManager] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [editingTagId, setEditingTagId] = useState(null);
  const [editingTagName, setEditingTagName] = useState('');
  const [tagSaving, setTagSaving] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editingLead, setEditingLead] = useState(null);
  const [intakeDetails, setIntakeDetails] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [savingLead, setSavingLead] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailForm, setEmailForm] = useState({ to: '', subject: '', body: '' });
  const [loadError, setLoadError] = useState('');
  const [isUploadingIntakePhotos, setIsUploadingIntakePhotos] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const crmSearchTimerRef = useRef(null);

  // Mass email state — leads are selected directly from the list
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
  const [showMassEmailCompose, setShowMassEmailCompose] = useState(false);
  const [massEmailSubject, setMassEmailSubject] = useState('');
  const [massEmailBody, setMassEmailBody] = useState('');
  const [massEmailSending, setMassEmailSending] = useState(false);
  const [massEmailResult, setMassEmailResult] = useState(null);

  const loadLeads = async (searchOverride) => {
    setLoading(true);
    try {
      const params = {};
      const searchValue = searchOverride !== undefined ? searchOverride : search;
      if (searchValue.trim()) params.search = searchValue.trim();
      if (filterTag) params.tag = filterTag;
      if (filterDivision) params.division_type = filterDivision;
      const response = await axios.get(`${API}/crm/leads`, { params });
      setLeads(response.data || []);
      setLeadsPage(1);
      setLoadError('');
    } catch (error) {
      if (error.response?.status === 401) {
        return;
      }
      console.error('Error loading leads:', error);
      setLoadError('Unable to refresh leads. Please reload.');
    } finally {
      setLoading(false);
    }
  };

  // Mass email helpers
  const toggleLeadSelection = (id) => {
    setSelectedLeadIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllLeads = () => {
    setSelectedLeadIds(new Set(leads.map(l => l.id)));
  };

  const clearLeadSelection = () => {
    setSelectedLeadIds(new Set());
  };

  const openMassEmailCompose = () => {
    setMassEmailSubject('');
    setMassEmailBody('');
    setMassEmailResult(null);
    setShowMassEmailCompose(true);
  };

  const sendMassEmail = async () => {
    if (selectedLeadIds.size === 0) { alert('Please select at least one lead.'); return; }
    if (!massEmailSubject.trim() || !massEmailBody.trim()) { alert('Please fill in both subject and message.'); return; }
    const selectedWithEmail = leads.filter(l => selectedLeadIds.has(l.id) && l.email?.trim());
    if (selectedWithEmail.length === 0) { alert('None of the selected leads have an email address.'); return; }
    if (!window.confirm(`Send this email to ${selectedWithEmail.length} lead(s)?`)) return;

    setMassEmailSending(true);
    setMassEmailResult(null);
    try {
      const response = await axios.post(`${API}/crm/mass-email-leads`, {
        lead_ids: Array.from(selectedLeadIds),
        subject: massEmailSubject,
        message: massEmailBody
      });
      setMassEmailResult(response.data);
    } catch (error) {
      console.error('Error sending mass email:', error);
      alert(error.response?.data?.detail || 'Failed to send mass email.');
    } finally {
      setMassEmailSending(false);
    }
  };

  const loadTags = async () => {
    try {
      const response = await axios.get(`${API}/crm/tags`);
      const tags = response.data || [];
      setTagOptions(tags.length ? tags : DEFAULT_TAG_OPTIONS.map((name) => ({ id: name, name })));
    } catch (error) {
      console.error('Error loading CRM tags:', error);
      setTagOptions(DEFAULT_TAG_OPTIONS.map((name) => ({ id: name, name })));
    }
  };

  useEffect(() => {
    loadLeads();
    loadTags();
  }, []);

  // Auto-refresh leads when filter changes
  useEffect(() => {
    loadLeads();
  }, [filterTag, filterDivision]);

  useEffect(() => {
    const handleResize = () => {
      // Treat anything below the Tailwind `lg` breakpoint (1024px) as "mobile" for this module
      // so that narrow desktop previews also use the modal pattern.
      setIsMobile(window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleOpenLead = async (lead) => {
    try {
      const response = await axios.get(`${API}/crm/leads/${lead.id}`);
      const fullLead = response.data;
      setSelectedLead(fullLead);
      setIntakeDetails(fullLead.intake_snapshot || null);
      setEditingLead({
        name: fullLead.name || '',
        email: fullLead.email || '',
        phone: fullLead.phone || '',
        address: fullLead.address || '',
        city: fullLead.city || '',
        province: fullLead.province || '',
        postal_code: fullLead.postal_code || '',
        division_type: fullLead.division_type || '',
        lead_source: fullLead.lead_source || '',
        tags: fullLead.tags || [],
        notes: fullLead.notes || '',
      });
      setNotes(fullLead.notes_timeline || []);
      setEmailForm({
        to: fullLead.email || '',
        subject: '',
        body: '',
      });
    } catch (error) {
      console.error('Error loading lead:', error);
      alert('Error loading lead details.');
    }
  };

  const handleNewLead = () => {
    setSelectedLead({ id: null });
    setIntakeDetails(null);
    setEditingLead({
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      province: '',
      postal_code: '',
      division_type: '',
      lead_source: '',
      tags: [],
      notes: '',
    });
    setNotes([]);
    setEmailForm({ to: '', subject: '', body: '' });
  };

  const handleSaveLead = async (e) => {
    e.preventDefault();
    if (!editingLead?.name) {
      alert('Name is required.');
      return;
    }

    setSavingLead(true);
    try {
      if (selectedLead?.id) {
        // update
        await axios.put(`${API}/crm/leads/${selectedLead.id}`, editingLead);
      } else {
        // create
        const response = await axios.post(`${API}/crm/leads`, editingLead);
        setSelectedLead(response.data);
      }
      await loadLeads();
      alert('Lead saved.');
    } catch (error) {
      console.error('Error saving lead:', error);
      alert('Error saving lead.');
    } finally {
      setSavingLead(false);
    }
  };

  const handleDeleteLead = async () => {
    if (!selectedLead?.id) return;
    if (!window.confirm('Delete this lead and all its notes?')) return;
    try {
      await axios.delete(`${API}/crm/leads/${selectedLead.id}`);
      setSelectedLead(null);
      setEditingLead(null);
      setNotes([]);
      await loadLeads();
      alert('Lead deleted.');
    } catch (error) {
      console.error('Error deleting lead:', error);
      alert('Error deleting lead.');
    }
  };

  const toggleTag = (tag) => {
    if (!editingLead) return;
    const hasTag = editingLead.tags?.includes(tag);
    const nextTags = hasTag
      ? editingLead.tags.filter((t) => t !== tag)
      : [...(editingLead.tags || []), tag];
    setEditingLead({ ...editingLead, tags: nextTags });
  };

  const handleAddTag = async (e) => {
    e.preventDefault();
    const name = newTagName.trim();
    if (!name) return;
    setTagSaving(true);
    try {
      await axios.post(`${API}/crm/tags`, { name });
      setNewTagName('');
      await loadTags();
    } catch (error) {
      console.error('Error creating tag:', error);
      alert('Error creating tag.');
    } finally {
      setTagSaving(false);
    }
  };

  const handleStartEditTag = (tag) => {
    setEditingTagId(tag.id);
    setEditingTagName(tag.name);
  };

  const handleUpdateTag = async (tag) => {
    const name = editingTagName.trim();
    if (!name) return;
    setTagSaving(true);
    try {
      await axios.put(`${API}/crm/tags/${tag.id}`, { name });
      if (filterTag === tag.name) {
        setFilterTag(name);
      }
      if (editingLead?.tags?.includes(tag.name)) {
        setEditingLead({
          ...editingLead,
          tags: editingLead.tags.map((t) => (t === tag.name ? name : t)),
        });
      }
      await loadTags();
      await loadLeads();
      setEditingTagId(null);
      setEditingTagName('');
    } catch (error) {
      console.error('Error updating tag:', error);
      alert('Error updating tag.');
    } finally {
      setTagSaving(false);
    }
  };

  const handleDeleteTag = async (tag) => {
    if (!window.confirm(`Delete tag "${tag.name}"?`)) return;
    setTagSaving(true);
    try {
      await axios.delete(`${API}/crm/tags/${tag.id}`);
      if (filterTag === tag.name) {
        setFilterTag('');
      }
      if (editingLead?.tags?.includes(tag.name)) {
        setEditingLead({
          ...editingLead,
          tags: editingLead.tags.filter((t) => t !== tag.name),
        });
      }
      await loadTags();
      await loadLeads();
    } catch (error) {
      console.error('Error deleting tag:', error);
      alert('Error deleting tag.');
    } finally {
      setTagSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!selectedLead?.id || !newNoteText.trim()) return;
    try {
      const response = await axios.post(`${API}/crm/leads/${selectedLead.id}/notes`, {
        text: newNoteText.trim(),
      });
      setNotes((prev) => [response.data, ...prev]);
      setNewNoteText('');
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Error adding note.');
    }
  };

  const handleUpdateNote = async (noteId, text) => {
    try {
      const response = await axios.put(`${API}/crm/notes/${noteId}`, { text });
      setNotes((prev) => prev.map((n) => (n.id === noteId ? response.data : n)));
    } catch (error) {
      console.error('Error updating note:', error);
      alert('Error updating note.');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API}/crm/notes/${noteId}`);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Error deleting note.');
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!selectedLead?.id) {
      alert('Save the lead before sending an email.');
      return;
    }
    if (!emailForm.to || !emailForm.subject || !emailForm.body) {
      alert('To, subject, and body are required.');
      return;
    }

    setSendingEmail(true);
    try {
      await axios.post(`${API}/crm/leads/${selectedLead.id}/send-email`, emailForm);
      alert('Email sent.');
      // reload notes timeline (email is logged as a note)
      await handleOpenLead({ id: selectedLead.id });
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Error sending email.');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleOpenIntakePhoto = async (file) => {
    if (!intakeDetails?.id) return;
    let previewWindow = null;
    if (typeof window !== 'undefined') {
      previewWindow = window.open('', '_blank');
    }
    try {
      const res = await axios.get(
        `${API}/intakes/${intakeDetails.id}/files/${file.id}`,
        { responseType: 'blob' }
      );
      const blobUrl = window.URL.createObjectURL(res.data);
      if (previewWindow) {
        previewWindow.location = blobUrl;
      } else {
        window.location.href = blobUrl;
      }
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 5000);
    } catch (err) {
      if (previewWindow) {
        previewWindow.close();
      }
      console.error('Error opening intake photo from CRM', err);
      alert('Unable to open intake photo.');
    }
  };

  const renderNotesTimeline = () => (
    <div className="space-y-3">
      {notes.length === 0 && (
        <p className="text-sm text-gray-500">No notes yet.</p>
      )}
      {notes.map((note) => (
        <LeadNoteItem
          key={note.id}
          note={note}
          onUpdate={handleUpdateNote}
          onDelete={handleDeleteNote}
        />
      ))}
    </div>
  );

  const renderIntakeSnapshotPanel = (variant = 'desktop') => {
    if (!intakeDetails) return null;
    const testIdPrefix = variant === 'mobile' ? 'crm-intake-mobile' : 'crm-intake-desktop';
    return (
      <div
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mt-4"
        data-testid={`${testIdPrefix}-details-panel`}
      >
        <h2
          className="text-sm font-semibold text-gray-700 mb-2"
          data-testid={`${testIdPrefix}-details-heading`}
        >
          Phone Intake Details
        </h2>
        <p className="text-xs text-gray-500 mb-3" data-testid={`${testIdPrefix}-details-subtitle`}>
          This snapshot comes from the most recent Phone Intake linked to this lead. You can edit key notes here for sales purposes.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs mb-3">
          <div data-testid={`${testIdPrefix}-division`}>
            <div className="text-gray-500">Division</div>
            <div className="font-medium text-gray-900">{intakeDetails.division || '-'}</div>
          </div>
          <div data-testid={`${testIdPrefix}-priority`}>
            <div className="text-gray-500">Priority</div>
            <div className="font-medium text-gray-900">{intakeDetails.priority || '-'}</div>
          </div>
          <div data-testid={`${testIdPrefix}-photos-received`}>
            <div className="text-gray-500">Photos</div>
            <div className="font-medium text-gray-900">{intakeDetails.photos_received || '-'}</div>
          </div>
        </div>

        <div className="mb-3">
          <label className="block text-xs font-semibold text-gray-600 mb-1">Issue Summary</label>
          <textarea
            rows={2}
            value={intakeDetails.issue_summary || ''}
            onChange={(e) =>
              setIntakeDetails((prev) => ({
                ...(prev || {}),
                issue_summary: e.target.value,
              }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs"
            data-testid={`${testIdPrefix}-issue-summary`}
          />
        </div>

        <div className="mb-3">
          <label className="block text-xs font-semibold text-gray-600 mb-1">Notes for Estimator</label>
          <textarea
            rows={2}
            value={intakeDetails.notes_for_estimator || ''}
            onChange={(e) =>
              setIntakeDetails((prev) => ({
                ...(prev || {}),
                notes_for_estimator: e.target.value,
              }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs"
            data-testid={`${testIdPrefix}-notes-for-estimator`}
          />
        </div>

        {intakeDetails.division === 'Foundations' && intakeDetails.foundations && (
          <div className="mb-3" data-testid={`${testIdPrefix}-foundations-questions`}>
            <h3 className="text-xs font-semibold text-gray-700 mb-1">Foundations Question Pack</h3>
            <div className="text-[11px] text-gray-600 space-y-1">
              {Array.isArray(intakeDetails.foundations.type_of_issue) && intakeDetails.foundations.type_of_issue.length > 0 && (
                <div>
                  <span className="font-semibold">Type of issue(s): </span>
                  <span>{intakeDetails.foundations.type_of_issue.join(', ')}</span>
                </div>
              )}
              {intakeDetails.foundations.issue_location && (
                <div>
                  <span className="font-semibold">Location of issue: </span>
                  <span>{intakeDetails.foundations.issue_location}</span>
                </div>
              )}
              {intakeDetails.foundations.structure_type && (
                <div>
                  <span className="font-semibold">Structure type: </span>
                  <span>{intakeDetails.foundations.structure_type}</span>
                </div>
              )}
              {intakeDetails.foundations.foundation_type && (
                <div>
                  <span className="font-semibold">Foundation type: </span>
                  <span>{intakeDetails.foundations.foundation_type}</span>
                </div>
              )}
              {intakeDetails.foundations.crawlspace_headroom && (
                <div>
                  <span className="font-semibold">Crawlspace headroom: </span>
                  <span>{intakeDetails.foundations.crawlspace_headroom}</span>
                </div>
              )}
              {intakeDetails.foundations.is_area_finished && (
                <div>
                  <span className="font-semibold">Area finished: </span>
                  <span>{intakeDetails.foundations.is_area_finished}</span>
                </div>
              )}
              {intakeDetails.foundations.exterior_obstructions && (
                <div>
                  <span className="font-semibold">Exterior obstructions: </span>
                  <span>{intakeDetails.foundations.exterior_obstructions}</span>
                </div>
              )}
              {intakeDetails.foundations.severity_notes && (
                <div>
                  <span className="font-semibold">Severity & symptoms: </span>
                  <span>{intakeDetails.foundations.severity_notes}</span>
                </div>
              )}
              {intakeDetails.foundations.additional_notes && (
                <div>
                  <span className="font-semibold">Additional notes: </span>
                  <span>{intakeDetails.foundations.additional_notes}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {intakeDetails.division === 'Waterproofing' && intakeDetails.waterproofing && (
          <div className="mb-3" data-testid={`${testIdPrefix}-waterproofing-questions`}>
            <h3 className="text-xs font-semibold text-gray-700 mb-1">Waterproofing Question Pack</h3>
            <div className="text-[11px] text-gray-600 space-y-1">
              {Object.entries(intakeDetails.waterproofing).map(([key, value]) => {
                if (!value || (Array.isArray(value) && value.length === 0)) return null;
                const displayValue = Array.isArray(value) ? value.join(', ') : String(value);
                return (
                  <div key={key}>
                    <span className="font-semibold">{key.replace(/_/g, ' ')}: </span>
                    <span>{displayValue}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {intakeDetails.division === 'Septics' && intakeDetails.septics && (
          <div className="mb-3" data-testid={`${testIdPrefix}-septics-questions`}>
            <h3 className="text-xs font-semibold text-gray-700 mb-1">Septics Question Pack</h3>
            <div className="text-[11px] text-gray-600 space-y-1">
              {Object.entries(intakeDetails.septics).map(([key, value]) =>
                value ? (
                  <div key={key}>
                    <span className="font-semibold">{key}: </span>
                    <span>{String(value)}</span>
                  </div>
                ) : null
              )}
            </div>
          </div>
        )}

        <div className="mb-3" data-testid={`${testIdPrefix}-photos`}>
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-xs font-semibold text-gray-700">Intake Photos</h3>
            {intakeDetails?.id && (
              <label
                className="inline-flex items-center justify-center px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md cursor-pointer text-[11px] font-semibold"
                data-testid={`${testIdPrefix}-upload-button`}
              >
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={async (event) => {
                    const files = Array.from(event.target.files || []);
                    if (!files.length) return;
                    if (!intakeDetails.id) {
                      alert('No linked intake found for this lead.');
                      event.target.value = '';
                      return;
                    }
                    const formData = new FormData();
                    files.forEach((file) => formData.append('files', file));
                    setIsUploadingIntakePhotos(true);
                    try {
                      const res = await axios.post(
                        `${API}/intakes/${intakeDetails.id}/upload-photos`,
                        formData,
                        { headers: { 'Content-Type': 'multipart/form-data' } }
                      );
                      const uploaded = res.data.uploaded_files || [];
                      setIntakeDetails((prev) => ({ ...(prev || {}), uploaded_files: uploaded }));
                    } catch (err) {
                      console.error('Error uploading photos from CRM', err);
                      alert('Error uploading intake photos.');
                    } finally {
                      setIsUploadingIntakePhotos(false);
                      event.target.value = '';
                    }
                  }}
                  data-testid={`${testIdPrefix}-upload-input`}
                />
                {isUploadingIntakePhotos ? 'Uploading…' : 'Upload Photos'}
              </label>
            )}
          </div>

          {Array.isArray(intakeDetails.uploaded_files) && intakeDetails.uploaded_files.length > 0 && (
            <div className="mt-1" data-testid={`${testIdPrefix}-photo-list`}>
              <IntakePhotoThumbnails
                intakeId={intakeDetails.id}
                files={intakeDetails.uploaded_files}
                testIdPrefix={testIdPrefix}
                onOpen={handleOpenIntakePhoto}
              />
              <div className="text-[10px] text-gray-500 mt-2" data-testid={`${testIdPrefix}-photo-note`}>
                Click a thumbnail to view full-size. Use arrow keys or buttons to browse.
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end mt-3">
          <button
            type="button"
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-semibold"
            onClick={async () => {
              if (!selectedLead?.id) return;
              try {
                const updatedSnapshot = { ...(intakeDetails || {}) };
                await axios.put(`${API}/crm/leads/${selectedLead.id}`, {
                  intake_snapshot: updatedSnapshot,
                });
                alert('Intake details updated on lead.');
              } catch (err) {
                console.error('Error updating intake details on lead', err);
                alert('Error updating intake details.');
              }
            }}
            data-testid={`${testIdPrefix}-save-button`}
          >
            Save Intake Details
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-800">CRM – Leads</h1>
          <div className="flex items-center gap-3">
            {selectedLeadIds.size > 0 && (
              <button
                onClick={openMassEmailCompose}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold text-sm"
                data-testid="crm-mass-email-btn"
              >
                Email {selectedLeadIds.size} Selected
              </button>
            )}
            <button
              onClick={handleNewLead}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold"
            >
              + New Lead
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Mobile lead detail modal */}
        {isMobile && isLeadModalOpen && editingLead && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
            <div className="bg-white w-full h-full max-w-md mx-auto shadow-xl flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
                <button
                  onClick={() => setIsLeadModalOpen(false)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  data-testid="crm-mobile-back-button"
                >
                  ← Back to list
                </button>
                <div className="text-sm font-semibold text-gray-800 truncate max-w-[60%]">
                  {editingLead.name || 'New Lead'}
                </div>
                <div className="w-8" />
              </div>
              <div
                className="flex-1 overflow-y-auto p-4 min-h-0"
                style={{ WebkitOverflowScrolling: 'touch' }}
              >
                {/* Reuse the same lead detail layout for mobile by rendering the form section only */}
                {editingLead && (
                  <div className="space-y-4">
                    {/* Basic fields */}
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Name</label>
                        <input
                          type="text"
                          value={editingLead.name}
                          onChange={(e) => setEditingLead({ ...editingLead, name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Division</label>
                        <select
                          value={editingLead.division_type}
                          onChange={(e) => setEditingLead({ ...editingLead, division_type: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          {DIVISION_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
                        <input
                          type="email"
                          value={editingLead.email}
                          onChange={(e) => setEditingLead({ ...editingLead, email: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Phone</label>
                        <input
                          type="text"
                          value={editingLead.phone}
                          onChange={(e) => setEditingLead({ ...editingLead, phone: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Address</label>
                        <input
                          type="text"
                          value={editingLead.address}
                          onChange={(e) => setEditingLead({ ...editingLead, address: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2"
                        />
                        <div className="grid grid-cols-3 gap-2">
                          <input
                            type="text"
                            placeholder="City"
                            value={editingLead.city}
                            onChange={(e) => setEditingLead({ ...editingLead, city: e.target.value })}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                          <input
                            type="text"
                            placeholder="Province"
                            value={editingLead.province}
                            onChange={(e) => setEditingLead({ ...editingLead, province: e.target.value })}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                          <input
                            type="text"
                            placeholder="Postal Code"
                            value={editingLead.postal_code}
                            onChange={(e) => setEditingLead({ ...editingLead, postal_code: e.target.value })}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    {renderIntakeSnapshotPanel('mobile')}

                    {/* Lead source & tags */}
                    <div className="grid grid-cols-1 gap-3 text-sm mt-2">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Lead Source</label>
                        <input
                          type="text"
                          value={editingLead.lead_source}
                          onChange={(e) => setEditingLead({ ...editingLead, lead_source: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Tags</label>
                        <div className="flex flex-wrap gap-2">
                          {tagOptions.map((tag) => {
                            const isActive = editingLead.tags?.includes(tag.name);
                            return (
                              <button
                                key={tag.id}
                                type="button"
                                onClick={() => toggleTag(tag.name)}
                                className={`px-2 py-1 rounded-full text-xs border ${
                                  isActive
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-gray-50 text-gray-700 border-gray-300'
                                }`}
                              >
                                {tag.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Internal notes */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Internal Notes</label>
                      <textarea
                        rows={3}
                        value={editingLead.notes}
                        onChange={(e) => setEditingLead({ ...editingLead, notes: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>

                    {/* Save/Delete */}
                    <div className="flex justify-between items-center gap-3 mt-2 lg:hidden">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={savingLead}
                          onClick={async () => {
                            // Reuse save logic
                            const fakeEvent = { preventDefault: () => {} };
                            await handleSaveLead(fakeEvent);
                          }}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold disabled:bg-gray-400"
                        >
                          {savingLead ? 'Saving...' : 'Save Lead'}
                        </button>
                        {selectedLead?.id && (
                          <button
                            type="button"
                            onClick={handleDeleteLead}
                            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                      {selectedLead?.last_contact_at && (
                        <div className="text-xs text-gray-500">
                          Last contact: {new Date(selectedLead.last_contact_at).toLocaleString()}
                        </div>
                      )}
                    </div>

                    {/* Notes & Activity */}
                    <div className="mt-4">
                      <h2 className="text-sm font-semibold text-gray-700 mb-2">Notes & Activity</h2>
                      <div className="flex gap-2 mb-2">
                        <textarea
                          rows={2}
                          value={newNoteText}
                          onChange={(e) => setNewNoteText(e.target.value)}
                          placeholder="Add a note..."
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <button
                          onClick={handleAddNote}
                          className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold"
                        >
                          Add
                        </button>
                      </div>
                      <div className="max-h-48 overflow-y-auto border-t border-gray-100 pt-2">
                        {renderNotesTimeline()}
                      </div>
                    </div>

                    {/* Send Email */}
                    <div className="mt-4">
                      <h2 className="text-sm font-semibold text-gray-700 mb-2">Send Email</h2>
                      <form onSubmit={handleSendEmail} className="space-y-2">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">To</label>
                          <input
                            type="email"
                            value={emailForm.to}
                            onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Subject</label>
                          <input
                            type="text"
                            value={emailForm.subject}
                            onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Body</label>
                          <textarea
                            rows={4}
                            value={emailForm.body}
                            onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div className="pt-1">
                          <button
                            type="submit"
                            disabled={sendingEmail}
                            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold disabled:bg-gray-400"
                          >
                            {sendingEmail ? 'Sending...' : 'Send Email'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {showTagManager && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60" data-testid="crm-tag-manager-modal">
            <div className="bg-white w-full max-w-lg mx-4 rounded-xl shadow-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-800">CRM Tags</h2>
                <button
                  onClick={() => setShowTagManager(false)}
                  className="text-gray-500 hover:text-gray-700"
                  data-testid="crm-tag-manager-close"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleAddTag} className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="New tag name"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="crm-tag-new-input"
                />
                <button
                  type="submit"
                  disabled={tagSaving}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold disabled:bg-gray-400"
                  data-testid="crm-tag-add-button"
                >
                  Add
                </button>
              </form>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {tagOptions.length === 0 && (
                  <div className="text-sm text-gray-500">No tags yet.</div>
                )}
                {tagOptions.map((tag) => (
                  <div key={tag.id} className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2">
                    {editingTagId === tag.id ? (
                      <input
                        type="text"
                        value={editingTagName}
                        onChange={(e) => setEditingTagName(e.target.value)}
                        className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                        data-testid={`crm-tag-edit-input-${tag.id}`}
                      />
                    ) : (
                      <div className="flex-1 text-sm font-medium text-gray-800">{tag.name}</div>
                    )}
                    {editingTagId === tag.id ? (
                      <button
                        type="button"
                        onClick={() => handleUpdateTag(tag)}
                        className="px-3 py-1.5 bg-green-600 text-white rounded-md text-xs font-semibold"
                        data-testid={`crm-tag-save-${tag.id}`}
                      >
                        Save
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleStartEditTag(tag)}
                        className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md text-xs font-semibold"
                        data-testid={`crm-tag-edit-${tag.id}`}
                      >
                        Edit
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDeleteTag(tag)}
                      className="px-3 py-1.5 border border-red-200 text-red-700 rounded-md text-xs font-semibold"
                      data-testid={`crm-tag-delete-${tag.id}`}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}


        {/* Mass Email Compose Modal */}
        {showMassEmailCompose && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60" data-testid="mass-email-modal">
            <div className="bg-white w-full max-w-lg mx-4 rounded-xl shadow-xl flex flex-col">
              <div className="flex items-center justify-between p-5 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-800">Email {selectedLeadIds.size} Lead{selectedLeadIds.size !== 1 ? 's' : ''}</h2>
                <button
                  onClick={() => setShowMassEmailCompose(false)}
                  className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
                  data-testid="mass-email-close"
                >&times;</button>
              </div>

              <div className="p-5 space-y-4">
                {/* Recipients preview */}
                <div className="text-xs text-gray-500">
                  <span className="font-semibold text-gray-700">To: </span>
                  {leads.filter(l => selectedLeadIds.has(l.id)).map(l => l.email ? `${l.name} <${l.email}>` : `${l.name} (no email)`).join(', ')}
                </div>

                <input
                  type="text"
                  placeholder="Subject line..."
                  value={massEmailSubject}
                  onChange={e => setMassEmailSubject(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="mass-email-subject"
                />
                <textarea
                  placeholder="Type your message here..."
                  value={massEmailBody}
                  onChange={e => setMassEmailBody(e.target.value)}
                  rows={6}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  data-testid="mass-email-body"
                />

                {massEmailResult && (
                  <div className={`rounded-lg p-4 text-sm ${massEmailResult.failed_count > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`} data-testid="mass-email-result">
                    <p className="font-semibold text-green-800">Sent successfully to {massEmailResult.sent_count} lead(s).</p>
                    {massEmailResult.failed_count > 0 && (
                      <p className="text-yellow-800 mt-1">Failed for {massEmailResult.failed_count}: {massEmailResult.failed.map(f => `${f.name || 'Unknown'} (${f.reason})`).join(', ')}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-200">
                <button
                  onClick={() => setShowMassEmailCompose(false)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
                >Cancel</button>
                <button
                  onClick={sendMassEmail}
                  disabled={massEmailSending || selectedLeadIds.size === 0}
                  className="px-6 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold"
                  data-testid="mass-email-send-btn"
                >
                  {massEmailSending ? 'Sending...' : `Send Email`}
                </button>
              </div>
            </div>
          </div>
        )}


        {/* Leads list */}
        <div className="lg:col-span-1 bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-col">
          <div className="mb-3">
            <input
              type="text"
              placeholder="Search by name, email, phone, address..."
              value={search}
              onChange={(e) => {
                const val = e.target.value;
                setSearch(val);
                if (crmSearchTimerRef.current) clearTimeout(crmSearchTimerRef.current);
                crmSearchTimerRef.current = setTimeout(() => loadLeads(val), 350);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="crm-leads-search"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3 lg:grid-cols-2">
            <select
              value={filterTag}
              onChange={(e) => setFilterTag(e.target.value)}
              className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="crm-leads-filter-tag"
            >
              <option value="">All tags</option>
              {tagOptions.map((tag) => (
                <option key={tag.id} value={tag.name}>{tag.name}</option>
              ))}
            </select>
            <select
              value={filterDivision}
              onChange={(e) => setFilterDivision(e.target.value)}
              className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="crm-leads-filter-division"
            >
              {DIVISION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <button
              onClick={loadLeads}
              className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm whitespace-nowrap"
              data-testid="crm-leads-refresh"
            >
              Refresh
            </button>
            {currentUser?.rawRole === 'admin' && (
              <button
                onClick={() => setShowTagManager(true)}
                className="w-full px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-sm whitespace-nowrap"
                data-testid="crm-manage-tags-button"
              >
                Manage Tags
              </button>
            )}
          </div>
          {loadError && (
            <div className="text-xs text-red-600 mb-2" data-testid="crm-leads-load-error">
              {loadError}
            </div>
          )}
          {/* Selection bar */}
          {leads.length > 0 && (
            <div className="flex items-center justify-between py-2 border-t border-gray-100 mt-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={leads.length > 0 && selectedLeadIds.size === leads.length}
                  onChange={() => {
                    if (selectedLeadIds.size === leads.length) clearLeadSelection();
                    else selectAllLeads();
                  }}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600"
                  data-testid="crm-select-all-leads"
                />
                <span className="text-xs text-gray-500">
                  {selectedLeadIds.size > 0
                    ? `${selectedLeadIds.size} of ${leads.length} selected`
                    : `Select all (${leads.length})`}
                </span>
              </div>
              {selectedLeadIds.size > 0 && (
                <button
                  onClick={clearLeadSelection}
                  className="text-xs text-gray-500 hover:text-gray-700"
                  data-testid="crm-clear-selection"
                >
                  Clear
                </button>
              )}
            </div>
          )}
          <div className="flex-1 overflow-y-auto">
            {loading && (
              <div className="py-4 text-center text-gray-500 text-sm">Loading leads...</div>
            )}
            {!loading && leads.length === 0 && (
              <div className="py-4 text-center text-gray-400 text-sm">No leads found.</div>
            )}
            {!loading && leads.slice((leadsPage - 1) * LEADS_PER_PAGE, leadsPage * LEADS_PER_PAGE).map((lead) => (
              <div
                key={lead.id}
                className={`flex items-center gap-2 px-3 py-2 border-b border-gray-100 text-sm hover:bg-blue-50 ${
                  selectedLead?.id === lead.id ? 'bg-blue-50' : ''
                } ${selectedLeadIds.has(lead.id) ? 'bg-green-50' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={selectedLeadIds.has(lead.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleLeadSelection(lead.id);
                  }}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 flex-shrink-0"
                  data-testid={`crm-lead-check-${lead.id}`}
                />
                <button
                  onClick={() => {
                    handleOpenLead(lead);
                    if (isMobile) {
                      setIsLeadModalOpen(true);
                    }
                  }}
                  className="flex-1 text-left min-w-0"
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-800 truncate">{lead.name || 'Unnamed lead'}</div>
                      <div className="text-xs text-gray-500 truncate">{lead.email || lead.phone || 'No contact info'}</div>
                    </div>
                    <div className="text-xs text-right flex-shrink-0 ml-2">
                      <div className="text-gray-500">{lead.division_type || '-'}</div>
                      <div className="flex flex-wrap gap-1 mt-1 justify-end">
                        {(lead.tags || []).slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-[10px] font-medium"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {!loading && leads.length > LEADS_PER_PAGE && (
            <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <span className="text-xs text-gray-500">
                {(leadsPage - 1) * LEADS_PER_PAGE + 1}-{Math.min(leadsPage * LEADS_PER_PAGE, leads.length)} of {leads.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setLeadsPage(p => Math.max(1, p - 1))}
                  disabled={leadsPage === 1}
                  className="px-2.5 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="crm-leads-prev-page"
                >
                  Prev
                </button>
                {Array.from({ length: Math.ceil(leads.length / LEADS_PER_PAGE) }, (_, i) => i + 1).map(pg => (
                  <button
                    key={pg}
                    onClick={() => setLeadsPage(pg)}
                    className={`px-2.5 py-1 text-xs rounded border ${leadsPage === pg ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 bg-white hover:bg-gray-100 text-gray-700'}`}
                    data-testid={`crm-leads-page-${pg}`}
                  >
                    {pg}
                  </button>
                ))}
                <button
                  onClick={() => setLeadsPage(p => Math.min(Math.ceil(leads.length / LEADS_PER_PAGE), p + 1))}
                  disabled={leadsPage >= Math.ceil(leads.length / LEADS_PER_PAGE)}
                  className="px-2.5 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="crm-leads-next-page"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Lead detail & notes */}
        <div className="lg:col-span-2 flex flex-col gap-4">
          {!editingLead && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-gray-500 text-sm">
              Select a lead from the list or click "New Lead" to get started.
            </div>
          )}

          {editingLead && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <form onSubmit={handleSaveLead} className="space-y-4">
                <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Name</label>
                    <input
                      type="text"
                      value={editingLead.name}
                      onChange={(e) => setEditingLead({ ...editingLead, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Division</label>
                    <select
                      value={editingLead.division_type}
                      onChange={(e) => setEditingLead({ ...editingLead, division_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      {DIVISION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
                    <input
                      type="email"
                      value={editingLead.email}
                      onChange={(e) => setEditingLead({ ...editingLead, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Phone</label>
                    <input
                      type="text"
                      value={editingLead.phone}
                      onChange={(e) => setEditingLead({ ...editingLead, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Address</label>
                    <input
                      type="text"
                      value={editingLead.address}
                      onChange={(e) => setEditingLead({ ...editingLead, address: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <input
                        type="text"
                        placeholder="City"
                        value={editingLead.city}
                        onChange={(e) => setEditingLead({ ...editingLead, city: e.target.value })}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Province"
                        value={editingLead.province}
                        onChange={(e) => setEditingLead({ ...editingLead, province: e.target.value })}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Postal Code"
                        value={editingLead.postal_code}
                        onChange={(e) => setEditingLead({ ...editingLead, postal_code: e.target.value })}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>

                {/* Phone Intake Details Panel - Conditional */}
                {renderIntakeSnapshotPanel('desktop')}

              <div className="grid grid-cols-1 gap-3 text-sm mt-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Lead Source</label>
                    <input
                      type="text"
                      value={editingLead.lead_source}
                      onChange={(e) => setEditingLead({ ...editingLead, lead_source: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Tags</label>
                    <div className="flex flex-wrap gap-2">
                      {tagOptions.map((tag) => {
                        const isActive = editingLead.tags?.includes(tag.name);
                        return (
                          <button
                            key={tag.id}
                            type="button"
                            onClick={() => toggleTag(tag.name)}
                            className={`px-2 py-1 rounded-full text-xs border ${
                              isActive
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-gray-50 text-gray-700 border-gray-300'
                            }`}
                          >
                            {tag.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Internal Notes</label>
                  <textarea
                    rows={3}
                    value={editingLead.notes}
                    onChange={(e) => setEditingLead({ ...editingLead, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>

                <div className="flex justify-between items-center gap-3 mt-4">
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={savingLead}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold disabled:bg-gray-400"
                    >
                      {savingLead ? 'Saving...' : 'Save Lead'}
                    </button>
                    {selectedLead?.id && (
                      <button
                        type="button"
                        onClick={handleDeleteLead}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                  {selectedLead?.last_contact_at && (
                    <div className="text-xs text-gray-500">
                      Last contact: {new Date(selectedLead.last_contact_at).toLocaleString()}
                    </div>
                  )}
                </div>
                </div>
                </div>
              </form>
            </div>
          )}

          {editingLead && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Notes / activity */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-col">
                <h2 className="text-sm font-semibold text-gray-700 mb-2">Notes & Activity</h2>
                <div className="flex gap-2 mb-3">
                  <textarea
                    rows={2}
                    value={newNoteText}
                    onChange={(e) => setNewNoteText(e.target.value)}
                    placeholder="Add a note..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <button
                    onClick={handleAddNote}
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold"
                  >
                    Add
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto border-t border-gray-100 pt-2">
                  {renderNotesTimeline()}
                </div>
              </div>

              {/* Email panel */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-col">
                <h2 className="text-sm font-semibold text-gray-700 mb-2">Send Email</h2>
                <form onSubmit={handleSendEmail} className="space-y-2 flex-1 flex flex-col">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">To</label>
                    <input
                      type="email"
                      value={emailForm.to}
                      onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Subject</label>
                    <input
                      type="text"
                      value={emailForm.subject}
                      onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div className="flex-1 flex flex-col">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Body</label>
                    <textarea
                      rows={4}
                      value={emailForm.body}
                      onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={sendingEmail}
                      className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold disabled:bg-gray-400"
                    >
                      {sendingEmail ? 'Sending...' : 'Send Email'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const LeadNoteItem = ({ note, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(note.text || '');
  const [expanded, setExpanded] = useState(false);

  const handleSave = async () => {
    if (!text.trim()) return;
    await onUpdate(note.id, text.trim());
    setIsEditing(false);
  };

  const isEmail = note.note_type === 'email';

  if (isEmail) {
    return (
      <div className="border border-blue-100 rounded-lg bg-blue-50 text-xs mb-1 overflow-hidden">
        {/* Email header */}
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-100 border-b border-blue-200">
          <svg className="w-3.5 h-3.5 text-blue-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span className="font-semibold text-blue-700">Email Sent</span>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => onDelete(note.id)}
              className="text-blue-400 hover:text-red-500 transition-colors"
              title="Delete"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* To / Subject */}
        <div className="px-3 py-2 space-y-0.5">
          {note.email_to && (
            <p className="text-gray-600"><span className="font-medium text-gray-700">To:</span> {note.email_to}</p>
          )}
          {note.email_subject && (
            <p className="text-gray-600"><span className="font-medium text-gray-700">Subject:</span> {note.email_subject}</p>
          )}
        </div>

        {/* Body — collapsible */}
        {note.text && (
          <div className="px-3 pb-2">
            <div
              className={`text-gray-700 whitespace-pre-wrap bg-white border border-blue-100 rounded p-2 ${expanded ? '' : 'max-h-16 overflow-hidden relative'}`}
            >
              {note.text}
              {!expanded && (
                <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white to-transparent" />
              )}
            </div>
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-1 text-blue-600 hover:text-blue-800 text-[10px] font-medium"
            >
              {expanded ? 'Show less' : 'Show full message'}
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="px-3 pb-2 text-[10px] text-gray-400 flex gap-2">
          <span>{note.author || 'Unknown'}</span>
          {note.created_at && <span>{new Date(note.created_at).toLocaleString()}</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-2 text-xs mb-1">
      <div className="flex justify-between items-start gap-2">
        <div className="flex-1">
          {isEditing ? (
            <textarea
              rows={2}
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded"
            />
          ) : (
            <p className="text-gray-800 whitespace-pre-wrap">{note.text}</p>
          )}
          <div className="mt-1 text-[10px] text-gray-500 flex gap-2">
            <span>{note.author || 'Unknown'}</span>
            {note.created_at && (
              <span>{new Date(note.created_at).toLocaleString()}</span>
            )}
            {note.updated_at && note.updated_at !== note.created_at && (
              <span>(edited)</span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {isEditing ? (
            <button
              onClick={handleSave}
              className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-[10px]"
            >
              Save
            </button>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-[10px]"
            >
              Edit
            </button>
          )}
          <button
            onClick={() => onDelete(note.id)}
            className="px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded text-[10px]"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// Sales Settings Module
const SalesSettingsModule = ({ onBack, initialTab = 'company' }) => {
  const [settings, setSettings] = useState(null);
  const [items, setItems] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [editingItem, setEditingItem] = useState(null);
  const [editingContract, setEditingContract] = useState(null);
  const [showContractForm, setShowContractForm] = useState(false);
  const [itemFormData, setItemFormData] = useState({
    id: '',
    name: '',
    description: '',
    private_notes: '',
    taxes: [],
    base_cost: 0,
    default_markup_percentage: 0,
    category: 'General'
  });
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [detailedView, setDetailedView] = useState(false);
  const [contractFormData, setContractFormData] = useState({
    id: '',
    name: '',
    content: '',
    is_default: false
  });

  const provinceOptions = [
    { code: 'ON', name: 'Ontario' },
    { code: 'AB', name: 'Alberta' },
    { code: 'BC', name: 'British Columbia' },
    { code: 'MB', name: 'Manitoba' },
    { code: 'NB', name: 'New Brunswick' },
    { code: 'NL', name: 'Newfoundland and Labrador' },
    { code: 'NS', name: 'Nova Scotia' },
    { code: 'PE', name: 'Prince Edward Island' },
    { code: 'QC', name: 'Quebec' },
    { code: 'SK', name: 'Saskatchewan' }
  ];

  useEffect(() => {
    loadSettings();
    loadItems();
    loadContracts();
  }, []);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/sales/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadItems = async () => {
    try {
      const response = await axios.get(`${API}/sales/items`);
      setItems(response.data);
    } catch (error) {
      console.error('Error loading items:', error);
    }
  };

  const loadContracts = async () => {
    try {
      const response = await axios.get(`${API}/sales/contracts`);
      setContracts(response.data);
    } catch (error) {
      console.error('Error loading contracts:', error);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/sales/settings`, settings);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTaxComponentChange = (province, index, field, value) => {
    setSettings(prev => {
      const currentRates = prev?.province_tax_rates?.[province] || [];
      const updatedRates = currentRates.map((component, idx) =>
        idx === index ? { ...component, [field]: value } : component
      );
      return {
        ...prev,
        province_tax_rates: {
          ...prev?.province_tax_rates,
          [province]: updatedRates
        }
      };
    });
  };

  const handleAddTaxComponent = (province) => {
    setSettings(prev => {
      const currentRates = prev?.province_tax_rates?.[province] || [];
      return {
        ...prev,
        province_tax_rates: {
          ...prev?.province_tax_rates,
          [province]: [...currentRates, { name: 'Tax', rate: 0 }]
        }
      };
    });
  };

  const handleRemoveTaxComponent = (province, index) => {
    setSettings(prev => {
      const currentRates = prev?.province_tax_rates?.[province] || [];
      const updatedRates = currentRates.filter((_, idx) => idx !== index);
      return {
        ...prev,
        province_tax_rates: {
          ...prev?.province_tax_rates,
          [province]: updatedRates
        }
      };
    });
  };

  const handleSaveItem = async (itemData) => {
    try {
      if (itemData.id && items.find(i => i.id === itemData.id)) {
        await axios.put(`${API}/sales/items/${itemData.id}`, itemData);
      } else {
        await axios.post(`${API}/sales/items`, itemData);
      }
      loadItems();
      setEditingItem(null);
      alert('Item saved successfully!');
    } catch (error) {
      console.error('Error saving item:', error);
      alert('Error saving item');
    }
  };

  const handleToggleItemTax = (taxOption) => {
    setItemFormData(prev => {
      const existing = prev.taxes?.find(tax => tax.name === taxOption.name);
      let updatedTaxes;
      if (existing) {
        updatedTaxes = prev.taxes.map(tax =>
          tax.name === taxOption.name ? { ...tax, enabled: !tax.enabled, rate: taxOption.rate } : tax
        );
      } else {
        updatedTaxes = [...(prev.taxes || []), { name: taxOption.name, rate: taxOption.rate, enabled: true }];
      }
      return { ...prev, taxes: updatedTaxes };
    });
  };

  const handleAddTaxOption = async () => {
    const name = prompt('Enter tax name');
    if (!name) return;
    const rateInput = prompt('Enter tax rate (%)');
    const rateValue = parseFloat(rateInput || '0');
    const rate = Number.isNaN(rateValue) ? 0 : rateValue;
    const updatedOptions = [...(settings?.item_tax_options || []), { name, rate }];
    const updatedSettings = { ...settings, item_tax_options: updatedOptions };
    setSettings(updatedSettings);
    try {
      await axios.put(`${API}/sales/settings`, updatedSettings);
      setSuccessMessage('Tax option added');
    } catch (error) {
      console.error('Error saving tax option:', error);
      setErrorMessage('Failed to save tax option');
    }
  };

  const filteredItems = items
    .filter(item => item.name?.toLowerCase().includes(itemSearchTerm.toLowerCase()))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const groupedItems = filteredItems.reduce((groups, item) => {
    const letter = (item.name || '#').charAt(0).toUpperCase();
    if (!groups[letter]) {
      groups[letter] = [];
    }
    groups[letter].push(item);
    return groups;
  }, {});

  const openItemForm = (item = null) => {
    if (item) {
      setEditingItem(item);
      setItemFormData({
        id: item.id,
        name: item.name || '',
        description: item.description || '',
        private_notes: item.private_notes || '',
        taxes: item.taxes || [],
        base_cost: item.base_cost || 0,
        default_markup_percentage: item.default_markup_percentage || 0,
        category: item.category || 'General'
      });
    } else {
      setEditingItem(null);
      setItemFormData({
        id: '',
        name: '',
        description: '',
        private_notes: '',
        taxes: [],
        base_cost: 0,
        default_markup_percentage: 0,
        category: 'General'
      });
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!itemId) { alert('Cannot delete item: missing ID'); return; }
    if (!confirm('Are you sure you want to delete this item?')) return;
    try {
      await axios.delete(`${API}/sales/items/${itemId}`);
      loadItems();
      openItemForm(null);
      alert('Item deleted successfully!');
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Error deleting item');
    }
  };

  const handleSaveContract = async (contractData) => {
    try {
      if (contractData.id && contracts.find(c => c.id === contractData.id)) {
        await axios.put(`${API}/sales/contracts/${contractData.id}`, contractData);
      } else {
        await axios.post(`${API}/sales/contracts`, contractData);
      }
      loadContracts();
      setShowContractForm(false);
      setEditingContract(null);
      alert('Contract saved successfully!');
    } catch (error) {
      console.error('Error saving contract:', error);
      alert('Error saving contract');
    }
  };

  const openContractForm = (contract = null) => {
    if (contract) {
      setEditingContract(contract);
      setContractFormData({
        id: contract.id,
        name: contract.name || '',
        content: contract.content || '',
        is_default: !!contract.is_default
      });
    } else {
      setEditingContract(null);
      setContractFormData({
        id: '',
        name: '',
        content: '',
        is_default: false
      });
    }
    setShowContractForm(true);
  };

  const handleDeleteContract = async (contractId) => {
    if (!confirm('Are you sure you want to delete this contract?')) return;
    try {
      await axios.delete(`${API}/sales/contracts/${contractId}`);
      loadContracts();
      alert('Contract deleted successfully!');
    } catch (error) {
      console.error('Error deleting contract:', error);
      alert('Error deleting contract');
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-xl text-gray-600">Loading settings...</div>
    </div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <h1 className="text-4xl font-bold text-gray-800">Sales Settings</h1>
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold disabled:bg-gray-400"
              data-testid="sales-settings-save"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mt-6 border-b">
            <button
              onClick={() => setActiveTab('company')}
              className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                activeTab === 'company'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
              data-testid="sales-settings-tab-company"
            >
              Company Info
            </button>
            <button
              onClick={() => setActiveTab('contracts')}
              className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                activeTab === 'contracts'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
              data-testid="sales-settings-tab-contracts"
            >
              Contract Templates
            </button>
            <button
              onClick={() => setActiveTab('items')}
              className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                activeTab === 'items'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
              data-testid="sales-settings-tab-items"
            >
              Item Library
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 py-8 max-w-4xl">
        {activeTab === 'company' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-6">Company Information</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                  <input
                    type="text"
                    value={settings?.company_name || ''}
                    onChange={(e) => setSettings({...settings, company_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    data-testid="sales-settings-company-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="text"
                    value={settings?.company_phone || ''}
                    onChange={(e) => setSettings({...settings, company_phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    data-testid="sales-settings-company-phone"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  type="text"
                  value={settings?.company_address || ''}
                  onChange={(e) => setSettings({...settings, company_address: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="sales-settings-company-address"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={settings?.company_city || ''}
                    onChange={(e) => setSettings({...settings, company_city: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    data-testid="sales-settings-company-city"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Province</label>
                  <input
                    type="text"
                    value={settings?.company_province || ''}
                    onChange={(e) => setSettings({...settings, company_province: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    data-testid="sales-settings-company-province"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                  <input
                    type="text"
                    value={settings?.company_postal_code || ''}
                    onChange={(e) => setSettings({...settings, company_postal_code: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    data-testid="sales-settings-company-postal"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={settings?.company_email || ''}
                    onChange={(e) => setSettings({...settings, company_email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    data-testid="sales-settings-company-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <input
                    type="text"
                    value={settings?.company_website || ''}
                    onChange={(e) => setSettings({...settings, company_website: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="sales-settings-company-website"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Default Estimate Email Message</label>
                <textarea
                  rows={4}
                  value={settings?.estimate_email_message || ''}
                  onChange={(e) => setSettings({ ...settings, estimate_email_message: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Write the default message that appears in estimate emails"
                  data-testid="sales-settings-estimate-email-message"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Default Invoice Email Message</label>
                <textarea
                  rows={4}
                  value={settings?.invoice_email_message || ''}
                  onChange={(e) => setSettings({ ...settings, invoice_email_message: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Write the default message that appears in invoice emails"
                  data-testid="sales-settings-invoice-email-message"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">HST/Business Number</label>
                <input
                  type="text"
                  value={settings?.hst_number || ''}
                  onChange={(e) => setSettings({...settings, hst_number: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="HST# 777835349RT0001"
                  data-testid="sales-settings-hst-number"
                />
              </div>

              {/* Default Credit Card Processing Fee */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Default Credit Card Processing Fee</h4>
                <p className="text-xs text-gray-500 mb-3">This default will pre-fill on new invoices. You can still enable/adjust it per invoice.</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Fee Type</label>
                    <select
                      value={settings?.default_cc_fee_type || 'percent'}
                      onChange={(e) => setSettings({ ...settings, default_cc_fee_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      data-testid="sales-settings-default-cc-fee-type"
                    >
                      <option value="percent">Percentage (%)</option>
                      <option value="flat">Flat Amount ($)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {settings?.default_cc_fee_type === 'flat' ? 'Default Amount ($)' : 'Default Rate (%)'}
                    </label>
                    <input
                      type="number"
                      value={settings?.default_cc_fee_value ?? ''}
                      onChange={(e) => setSettings({ ...settings, default_cc_fee_value: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder={settings?.default_cc_fee_type === 'flat' ? 'e.g. 5.00' : 'e.g. 2.9'}
                      step="0.01"
                      min="0"
                      data-testid="sales-settings-default-cc-fee-value"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-8" data-testid="sales-settings-province-taxes">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Province Tax Presets</h3>
                <div className="space-y-6">
                  {provinceOptions.map((province) => {
                    const components = settings?.province_tax_rates?.[province.code] || [];
                    const totalRate = components.reduce((sum, component) => sum + (parseFloat(component.rate) || 0), 0);
                    return (
                      <div key={province.code} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <div className="font-semibold text-gray-800" data-testid={`sales-tax-province-${province.code}`}>{province.name} ({province.code})</div>
                            <div className="text-xs text-gray-500" data-testid={`sales-tax-total-${province.code}`}>Total: {totalRate.toFixed(3)}%</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleAddTaxComponent(province.code)}
                            className="px-3 py-1.5 border border-blue-300 text-blue-700 rounded-md text-sm"
                            data-testid={`sales-tax-add-${province.code}`}
                          >
                            + Add Tax
                          </button>
                        </div>
                        {components.length === 0 ? (
                          <div className="text-xs text-gray-500" data-testid={`sales-tax-empty-${province.code}`}>No tax components set.</div>
                        ) : (
                          <div className="space-y-2">
                            {components.map((component, index) => (
                              <div key={`${province.code}-${index}`} className="grid grid-cols-3 gap-3 items-center">
                                <input
                                  type="text"
                                  value={component.name}
                                  onChange={(e) => handleTaxComponentChange(province.code, index, 'name', e.target.value)}
                                  className="px-3 py-2 border border-gray-300 rounded-lg"
                                  data-testid={`sales-tax-name-${province.code}-${index}`}
                                />
                                <input
                                  type="number"
                                  value={component.rate}
                                  onChange={(e) => handleTaxComponentChange(province.code, index, 'rate', parseFloat(e.target.value) || 0)}
                                  className="px-3 py-2 border border-gray-300 rounded-lg"
                                  step="0.001"
                                  data-testid={`sales-tax-rate-${province.code}-${index}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => handleRemoveTaxComponent(province.code, index)}
                                  className="px-3 py-2 border border-red-300 text-red-600 rounded-md text-sm"
                                  data-testid={`sales-tax-remove-${province.code}-${index}`}
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'contracts' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-800" data-testid="sales-contracts-title">Contract Templates</h2>
                  <p className="text-sm text-gray-500" data-testid="sales-contracts-subtitle">Maintain default terms for estimates and invoices.</p>
                </div>
                <button
                  onClick={() => openContractForm(null)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold"
                  data-testid="sales-contracts-new-button"
                >
                  + New Contract
                </button>
              </div>

              {contracts.length === 0 ? (
                <div className="text-sm text-gray-500" data-testid="sales-contracts-empty">No contract templates yet.</div>
              ) : (
                <div className="space-y-3">
                  {contracts.map(contract => (
                    <div
                      key={contract.id}
                      className="border border-gray-200 rounded-lg p-4 flex items-start justify-between gap-4"
                      data-testid={`sales-contract-card-${contract.id}`}
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-800" data-testid={`sales-contract-name-${contract.id}`}>{contract.name}</h3>
                          {contract.is_default && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded" data-testid={`sales-contract-default-${contract.id}`}>
                              DEFAULT
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-2" data-testid={`sales-contract-preview-${contract.id}`}>
                          {contract.content?.substring(0, 160)}...
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => openContractForm(contract)}
                          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                          data-testid={`sales-contract-edit-${contract.id}`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteContract(contract.id)}
                          className="px-3 py-1.5 border border-red-300 rounded-md text-sm text-red-600 hover:bg-red-50"
                          data-testid={`sales-contract-delete-${contract.id}`}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {showContractForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-16 sm:pt-4 overflow-y-auto" data-testid="sales-contract-form-modal">
                <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full">
                  <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                    <h3 className="text-lg font-bold text-gray-800" data-testid="sales-contract-form-title">
                      {editingContract ? 'Edit Contract' : 'New Contract'}
                    </h3>
                    <button
                      onClick={() => setShowContractForm(false)}
                      className="text-gray-500 hover:text-gray-700"
                      data-testid="sales-contract-form-close"
                    >
                      ✕
                    </button>
                  </div>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSaveContract(contractFormData);
                    }}
                    className="p-6 space-y-4"
                  >
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contract Name</label>
                      <input
                        type="text"
                        value={contractFormData.name}
                        onChange={(e) => setContractFormData({ ...contractFormData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        required
                        data-testid="sales-contract-form-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contract Content</label>
                      <textarea
                        rows={8}
                        value={contractFormData.content}
                        onChange={(e) => setContractFormData({ ...contractFormData, content: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        required
                        data-testid="sales-contract-form-content"
                      />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-700" data-testid="sales-contract-form-default">
                      <input
                        type="checkbox"
                        checked={contractFormData.is_default}
                        onChange={(e) => setContractFormData({ ...contractFormData, is_default: e.target.checked })}
                      />
                      Set as default template
                    </label>
                    <div className="flex justify-end gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => setShowContractForm(false)}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700"
                        data-testid="sales-contract-form-cancel"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold"
                        data-testid="sales-contract-form-save"
                      >
                        Save Contract
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'items' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search by item name"
                  value={itemSearchTerm}
                  onChange={(e) => setItemSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-full"
                  data-testid="sales-items-search"
                />
              </div>
              <button
                onClick={() => openItemForm(null)}
                className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-full font-semibold"
                data-testid="sales-items-new-button"
              >
                New Item
              </button>
            </div>

            <div className="grid lg:grid-cols-[280px,1fr] gap-6">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 h-[600px] flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Detailed View</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={detailedView}
                        onChange={(e) => setDetailedView(e.target.checked)}
                        data-testid="sales-items-detailed-toggle"
                      />
                      <div className="w-10 h-5 bg-gray-200 rounded-full peer-checked:bg-green-500 transition-colors"></div>
                      <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transform peer-checked:translate-x-5 transition-transform"></div>
                    </label>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto pr-2 space-y-3">
                  {Object.keys(groupedItems).length === 0 && (
                    <div className="text-sm text-gray-500">No items found.</div>
                  )}
                  {Object.entries(groupedItems).map(([letter, group]) => (
                    <div key={letter}>
                      <div className="text-xs font-semibold text-gray-500 mb-2">{letter}</div>
                      <div className="space-y-2">
                        {group.map(item => (
                          <div
                            key={item.id}
                            className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-white hover:bg-gray-100 group"
                            data-testid={`sales-item-card-${item.id}`}
                          >
                            <button
                              onClick={() => openItemForm(item)}
                              className="flex-1 text-left"
                            >
                              <div className="font-medium text-gray-800" data-testid={`sales-item-name-${item.id}`}>{item.name}</div>
                              {detailedView && (
                                <div className="text-xs text-gray-500" data-testid={`sales-item-cost-${item.id}`}>${formatCurrency(item.base_cost)}</div>
                              )}
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteItem(item.id); }}
                              className="opacity-0 group-hover:opacity-100 ml-2 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-opacity"
                              data-testid={`sales-item-delete-${item.id}`}
                              title="Delete item"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-6 space-y-6 max-h-[600px] overflow-y-auto">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Basic Information</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-500">Item Name</label>
                      <input
                        type="text"
                        value={itemFormData.name}
                        onChange={(e) => setItemFormData({ ...itemFormData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        data-testid="sales-item-name"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Rate</label>
                      <input
                        type="number"
                        step="0.01"
                        value={itemFormData.base_cost}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Use Number() and toFixed() for more reliable precision
                          const numValue = value === '' ? 0 : Number(Number(value).toFixed(2));
                          setItemFormData({ ...itemFormData, base_cost: numValue });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        data-testid="sales-item-base-cost"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Taxes</h4>
                  <div className="space-y-3">
                    {(settings?.item_tax_options || []).map((taxOption, index) => {
                      const enabled = itemFormData.taxes?.find(tax => tax.name === taxOption.name)?.enabled;
                      return (
                        <div key={`${taxOption.name}-${index}`} className="flex items-center justify-between">
                          <span className="text-sm text-gray-700">{taxOption.name} ({taxOption.rate}%)</span>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={!!enabled}
                              onChange={() => handleToggleItemTax(taxOption)}
                              data-testid={`sales-item-tax-toggle-${index}`}
                            />
                            <div className="w-10 h-5 bg-gray-200 rounded-full peer-checked:bg-green-500 transition-colors"></div>
                            <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transform peer-checked:translate-x-5 transition-transform"></div>
                          </label>
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      onClick={handleAddTaxOption}
                      className="inline-flex items-center gap-2 text-green-700 font-medium"
                      data-testid="sales-item-add-tax"
                    >
                      + Add New Tax
                    </button>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-gray-800">Markup</h4>
                    <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">PRO</span>
                  </div>
                  <div className="mt-2">
                    <label className="text-xs text-gray-500">Default Markup %</label>
                    <input
                      type="number"
                      value={itemFormData.default_markup_percentage}
                      onChange={(e) => setItemFormData({ ...itemFormData, default_markup_percentage: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      data-testid="sales-item-markup"
                    />
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Description</h4>
                  <textarea
                    placeholder="Item details"
                    value={itemFormData.description}
                    onChange={(e) => setItemFormData({ ...itemFormData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg h-28"
                    data-testid="sales-item-description"
                  />
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-gray-800">Private Notes</h4>
                    <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">PRO</span>
                  </div>
                  <textarea
                    placeholder="Type your note here - private notes do not appear on documents"
                    value={itemFormData.private_notes}
                    onChange={(e) => setItemFormData({ ...itemFormData, private_notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg h-24"
                    data-testid="sales-item-private-notes"
                  />
                </div>

                <div className="flex items-center justify-between">
                  {itemFormData.id && (
                    <button
                      onClick={() => handleDeleteItem(itemFormData.id)}
                      className="px-6 py-2 rounded-full bg-red-500 text-white"
                      data-testid="sales-item-delete"
                    >
                      Delete
                    </button>
                  )}
                  <div className="flex gap-3 ml-auto">
                    <button
                      onClick={() => openItemForm(null)}
                      className="px-4 py-2 rounded-full border border-gray-300 text-gray-600"
                      data-testid="sales-item-cancel"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleSaveItem(itemFormData)}
                      className="px-5 py-2 rounded-full bg-green-600 text-white"
                      data-testid="sales-item-save"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export { SalesLandingPage, CRMModule, SalesSettingsModule };
